import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription, PageContent } from '@/components/ui/page'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Phone, PhoneIncoming, PhoneOutgoing, Trash2 } from 'lucide-react'
import { useState, useMemo, useEffect } from 'react'
import { DataView, DataViewToolbar, type DataViewColumn, type DataViewAction, type ViewMode } from '@/components/data-view'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { TranscriptSheet } from '@/components/TranscriptSheet'
import { useTranscript } from '@/hooks/useTranscript'
import { getActionLabel, getSimpleActionLabel } from '@/utils/actionLabels'
import { CallLogSortField, SortDirection, CallDirection, ChannelType, type CallLogResponseDTO, type CallLogSearchResultDTO } from '@/api/generated/models'
import { formatRelativeDateTime, formatUTCToLocalDateTime, calculateDuration } from '@/lib/dateUtils'
import { useCallLogStream } from '@/hooks/useCallLogStream'
import { LiveIndicator } from '@/components/LiveIndicator'
import { useOrganization } from '@/hooks/useOrganization'
import { useCallLogsPage, useCallLogSearchPage } from '@/hooks/useCallLogsPage'
import { useDebounce } from '@/hooks/useDebounce'
import { useCallLogMutations } from '@/hooks/useCallLogMutations'
import { TablePagination } from '@/components/ui/table-pagination'
import { AktionBadge } from '@/components/AktionBadge'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

export const Route = createFileRoute('/calls')({
  component: Calls,
  errorComponent: RouteErrorFallback,
})

interface CallRow {
  id: string
  direction: string
  name: string
  nummer: string
  datum: string
  datumExact: string
  dauer: string
  turns: number
  userTurns: number
  agentTurns: number
  aktion: string
  startedAt: string
  reason: string | null
}

// Helper function to format caller name from API data
function formatCallerName(firstName: string | null, lastName: string | null): string {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`
  } else if (firstName) {
    return firstName
  } else if (lastName) {
    return lastName
  } else {
    return 'Unbekannt'
  }
}

function DirectionBadge({ direction }: { direction: string }) {
  if (direction === 'outbound') {
    return (
      <div className="flex items-center gap-1.5 text-blue-600">
        <PhoneOutgoing className="h-4 w-4" />
        <span className="text-xs font-medium">Ausgehend</span>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-1.5 text-green-600">
      <PhoneIncoming className="h-4 w-4" />
      <span className="text-xs font-medium">Eingehend</span>
    </div>
  )
}

function Calls() {
  const [view, setView] = useLocalStorage<ViewMode>('audavo:view:calls', 'table')
  const [directionFilter, setDirectionFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCallLogId, setSelectedCallLogId] = useState<string | null>(null)
  const [isTranscriptSheetOpen, setIsTranscriptSheetOpen] = useState(false)

  // Connect to SSE stream for real-time call log updates
  const { isConnected } = useCallLogStream()

  // Delete mutation hook
  const { deleteCallLog } = useCallLogMutations()

  // Fetch organization data for the headline
  const { data: organization } = useOrganization()

  // Backend full-text search kicks in once the (debounced) query is non-empty.
  const debouncedSearch = useDebounce(searchTerm.trim(), 300)
  const isSearching = debouncedSearch.length > 0

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== 'undefined' && window.innerHeight >= 900 ? 50 : 20
  )

  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, directionFilter, pageSize])

  // Fetch one page of call logs (paused while searching)
  const listQuery = useCallLogsPage({
    page,
    pageSize,
    directionFilter: directionFilter as CallDirection | 'all',
    sortField: CallLogSortField.StartedAt,
    sortDirection: SortDirection.Desc,
    channel: ChannelType.Phone,
    enabled: !isSearching,
  })

  // Backend search (paused unless there is a query)
  const searchQuery = useCallLogSearchPage({
    page,
    pageSize,
    q: debouncedSearch,
    channel: ChannelType.Phone,
    enabled: isSearching,
  })

  const {
    data,
    isLoading: isLoadingCalls,
    error: callsError,
  } = isSearching ? searchQuery : listQuery

  const allCallLogs = useMemo((): (CallLogResponseDTO | CallLogSearchResultDTO)[] => {
    return data?.results ?? []
  }, [data])

  const total = data?.total ?? 0

  // Map API data into the row view-model
  const rows = useMemo((): CallRow[] => {
    return allCallLogs.map((call: any) => {
      const action = call.action_type && call.action_classname
        ? { type: call.action_type, classname: call.action_classname }
        : null

      return {
        id: call.id,
        direction: call.call_direction || 'inbound',
        name: formatCallerName(call.caller_first_name, call.caller_last_name),
        nummer: call.remote_phone_number || 'Unbekannt',
        datum: formatRelativeDateTime(call.started_at),
        datumExact: formatUTCToLocalDateTime(call.started_at),
        dauer: calculateDuration(call.started_at, call.finished_at),
        userTurns: call.user_turns ?? 0,
        agentTurns: call.agent_turns ?? 0,
        turns: (call.user_turns ?? 0) + (call.agent_turns ?? 0),
        aktion: action ? getActionLabel(action) : getSimpleActionLabel(!!call.action_id),
        startedAt: call.started_at,
        reason: call.call_reason ?? null,
      }
    })
  }, [allCallLogs])

  // The search endpoint ignores the direction filter, so apply it client-side on results.
  const filteredRows = useMemo(() => {
    if (isSearching && directionFilter !== 'all') {
      return rows.filter((r) => r.direction === directionFilter)
    }
    return rows
  }, [rows, isSearching, directionFilter])

  // Fetch transcript for selected call log (updates automatically via SSE events)
  const { data: transcript, isLoading: isLoadingTranscript, error: transcriptError } = useTranscript(
    selectedCallLogId,
    isTranscriptSheetOpen
  )

  // Started-at of the selected call for the dialog header
  const selectedStartedAt = useMemo(() => {
    if (!selectedCallLogId) return undefined
    return rows.find((r) => r.id === selectedCallLogId)?.startedAt
  }, [selectedCallLogId, rows])

  const handleItemClick = (row: CallRow) => {
    setSelectedCallLogId(row.id)
    setIsTranscriptSheetOpen(true)
  }

  const columns: DataViewColumn<CallRow>[] = [
    {
      id: 'direction',
      header: 'Richtung',
      width: '130px',
      cell: (row) => <DirectionBadge direction={row.direction} />,
    },
    {
      id: 'name',
      header: 'Name',
      className: 'font-medium',
      cell: (row) => row.name,
    },
    {
      id: 'nummer',
      header: 'Nummer',
      width: '160px',
      cell: (row) => row.nummer,
    },
    {
      id: 'datum',
      header: 'Datum & Uhrzeit',
      width: '200px',
      cell: (row) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">{row.datum}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{row.datumExact}</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      id: 'dauer',
      header: 'Dauer',
      width: '100px',
      cell: (row) => row.dauer,
    },
    {
      id: 'turns',
      header: 'Interaktionen',
      width: '140px',
      cell: (row) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default tabular-nums">{row.turns}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{row.userTurns} Kunde · {row.agentTurns} Agent</p>
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      id: 'aktion',
      header: 'Aktion',
      width: '180px',
      cell: (row) => <AktionBadge aktion={row.aktion} />,
    },
  ]

  const actions: DataViewAction<CallRow>[] = [
    {
      id: 'delete',
      label: 'Löschen',
      icon: Trash2,
      variant: 'destructive',
      onSelect: (row) => {
        deleteCallLog.mutate(row.id)
      },
      confirm: {
        title: 'Anruf löschen?',
        description: (row) =>
          `Sind Sie sicher, dass Sie diesen Anruf vom ${row.datum} und das zugehörige Transkript löschen möchten? Tickets, Kontakte und Termine werden nicht gelöscht. Dieser Vorgang kann nicht rückgängig gemacht werden.`,
        confirmLabel: 'Löschen',
      },
    },
  ]

  const directionFilterControl = (
    <Select value={directionFilter} onValueChange={setDirectionFilter}>
      <SelectTrigger className="w-full sm:w-[180px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            <span>Alle Anrufe</span>
          </div>
        </SelectItem>
        <SelectItem value={CallDirection.Inbound}>
          <div className="flex items-center gap-2">
            <PhoneIncoming className="h-4 w-4" />
            <span>Eingehend</span>
          </div>
        </SelectItem>
        <SelectItem value={CallDirection.Outbound}>
          <div className="flex items-center gap-2">
            <PhoneOutgoing className="h-4 w-4" />
            <span>Ausgehend</span>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  )

  const paginationControl = (
    <TablePagination
      currentPage={page + 1}
      totalPages={Math.max(1, Math.ceil(total / pageSize))}
      totalItems={total}
      pageSize={pageSize}
      onPageChange={(p) => setPage(p - 1)}
      onPageSizeChange={setPageSize}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
    />
  )

  const showPagination = !isLoadingCalls && !callsError && (filteredRows.length > 0 || page > 0)

  const renderCard = (row: CallRow) => (
    <Card className="h-full">
      {/* Rechts halten wir auf Touch Platz fuer das Ecken-Aktionsmenue frei. */}
      <CardContent className="flex flex-col gap-3 p-4 pointer-coarse:pr-12">
        <div className="flex items-center justify-between gap-2">
          <DirectionBadge direction={row.direction} />
          <AktionBadge aktion={row.aktion} />
        </div>
        <div>
          <div className="font-medium">{row.name}</div>
          <div className="text-sm text-muted-foreground">{row.nummer}</div>
        </div>
        {/* In der Tabelle steht der exakte Zeitpunkt nur im Tooltip — auf Touch
            gibt es keinen, deshalb hier direkt. */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{row.datumExact}</span>
          <span>{row.dauer}</span>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle hiddenOnMobile>
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <PageHeaderHeading>{organization?.name ?? 'Laden...'}</PageHeaderHeading>
                <LiveIndicator isLive={isConnected} />
              </div>
              <PageHeaderDescription>Übersicht aller ein- und ausgehenden Anrufe</PageHeaderDescription>
            </PageHeaderTitle>
            <DataViewToolbar
              view={view}
              onViewChange={setView}
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: 'Name, Nummer, Grund…',
              }}
              filters={directionFilterControl}
            />
          </PageHeaderContent>
        </PageHeader>

        <PageContent className="p-6">
          <TooltipProvider>
            <DataView
              items={filteredRows}
              getRowId={(row) => row.id}
              columns={columns}
              renderCard={renderCard}
              actions={actions}
              onItemClick={handleItemClick}
              storageKey="audavo:view:calls"
              view={view}
              onViewChange={setView}
              isLoading={isLoadingCalls}
              error={callsError}
              emptyState={
                isSearching
                  ? {
                      icon: Phone,
                      title: 'Keine Treffer',
                      description: `Für „${debouncedSearch}“ wurden keine Anrufe gefunden.`,
                    }
                  : {
                      icon: Phone,
                      title: 'Keine Anrufe',
                      description: 'Es wurden noch keine Anrufe aufgezeichnet.',
                    }
              }
              pagination={showPagination ? paginationControl : undefined}
            />
          </TooltipProvider>
        </PageContent>
      </PageCard>

      <TranscriptSheet
        transcript={transcript ?? null}
        callLogId={selectedCallLogId ?? undefined}
        callLogDate={selectedStartedAt}
        isLoading={isLoadingTranscript}
        error={transcriptError ?? null}
        open={isTranscriptSheetOpen}
        onOpenChange={setIsTranscriptSheetOpen}
      />
    </PageContainer>
  )
}
